/**
 * Utility for formatting financial period values.
 * Converts YYYY-Q# format to Q# YYYY display format per design requirements.
 */
export class FinancialPeriodFormatter {
  /**
   * Format financial period from internal format (YYYY-Q#) to display format (Q# YYYY).
   * Example: '2024-Q4' -> 'Q4 2024'
   *
   * @param internalFormat The internal format (YYYY-Q# or raw quarter string)
   * @returns The display format (Q# YYYY) or the original string if it cannot be parsed
   */
  static format(internalFormat: string): string {
    if (!internalFormat || typeof internalFormat !== 'string') {
      return internalFormat;
    }

    // Match YYYY-Q# pattern
    const match = internalFormat.match(/^(\d{4})-Q([1-4])$/);
    if (match) {
      const [, year, quarter] = match;
      return `Q${quarter} ${year}`;
    }

    // If it's already in Q# YYYY format or another format, return as-is
    return internalFormat;
  }

  /**
   * Convert internal period format (YYYY-Q#) to the canonical API format (Q#YYYY).
   * Example: '2026-Q1' -> 'Q12026', '2024-Q4' -> 'Q42024'
   *
   * If the value is already in Q#YYYY format or cannot be parsed, it is returned unchanged.
   * This conversion is applied at the provider boundary only and must not affect
   * frontend route params, internal state, or display text.
   *
   * @param internalFormat The internal format (YYYY-Q# or already-canonical Q#YYYY string)
   * @returns The API-canonical format (Q#YYYY) or the original string if not convertible
   */
  static toApiPeriod(internalFormat: string): string {
    if (!internalFormat || typeof internalFormat !== 'string') {
      return internalFormat;
    }

    // Match YYYY-Q# pattern and convert to Q#YYYY
    const match = internalFormat.match(/^(\d{4})-Q([1-4])$/);
    if (match) {
      const [, year, quarter] = match;
      return `Q${quarter}${year}`;
    }

    // Already canonical (Q#YYYY), unknown format, or empty — return unchanged
    return internalFormat;
  }

  /**
   * Convert quarter notation into the API business reporting period.
   * Example: 'Q1 2026' or '2026-Q1' -> 202603.
   */
  static toBusinessReportingPeriod(period: string): number | null {
    if (!period || typeof period !== 'string') {
      return null;
    }

    const normalized = period.trim().toUpperCase().replace(/\s+/g, '');
    if (/^\d{6}$/.test(normalized)) {
      return Number(normalized);
    }

    const qFirst = normalized.match(/^Q([1-4])(\d{4})$/);
    if (qFirst) {
      const quarter = Number(qFirst[1]);
      return Number(`${qFirst[2]}${String(quarter * 3).padStart(2, '0')}`);
    }

    const yearFirst = normalized.match(/^(\d{4})-?Q([1-4])$/);
    if (yearFirst) {
      const quarter = Number(yearFirst[2]);
      return Number(`${yearFirst[1]}${String(quarter * 3).padStart(2, '0')}`);
    }

    return null;
  }
}
